import { listDataStreamPoints } from "@/api/orval/generated/data-stream";
import type { StreamPoint } from "../stream-cache/stream-cache-store";
import type { FetchedStream } from "./types";

const PAGE_LIMIT = 5000;

/**
 * Runaway guard on the pagination loop. The cursor-advance check below already guarantees
 * termination, so this only trips if the API starts behaving pathologically — at PAGE_LIMIT per
 * page it sits far above any window a chart can plot.
 */
const MAX_PAGES = 1000;

/** One point from the non-bucketed response shape. */
type RawPoint = { occurredAt: Date; convertedValue: number };

export async function fetchSingleStreamData(
    streamId: number,
    from: Date | string | number,
    to: Date | string | number,

    // Eventually, we'll pass this onto the API
    // resolution: ResolutionLabel,
): Promise<FetchedStream> {
    const windowStart = new Date(from);
    const windowEnd = new Date(to);

    const allPoints: RawPoint[] = [];
    /** Newest `occurredAt` seen across all pages — the coverage watermark for the caller. */
    let lastOccurredAt: Date | undefined;
    /** Exclusive lower bound for the next page; undefined until the first page has returned. */
    let pageStart: Date | undefined;

    for (let pageIndex = 0; pageIndex < MAX_PAGES; pageIndex += 1) {
        const page = await listDataStreamPoints(streamId, {
            "timeRange[start]": pageStart ?? windowStart,
            "timeRange[end]": windowEnd,
            // The initial graph window includes both selected endpoints. Subsequent
            // pages move only the lower edge to the cursor, exclusively, to avoid
            // returning the last point of the prior page again.
            "timeRange[startInclusive]": pageStart === undefined,
            "timeRange[endInclusive]": true,
            limit: PAGE_LIMIT,
        });

        // This endpoint returns a union of the raw and bucketed shapes, discriminated by `bucketing`.
        // This function never sends `bucketWidth`, so only the raw branch is reachable; checking it
        // narrows the union and would surface an API contract change rather than mistyping it.
        if (page.bucketing !== null) {
            throw new Error(`Unexpected bucketed response for stream ${streamId}`);
        }

        // `cursor` is the occurredAt of the last delivered point, or null for an empty page.
        const { points, cursor } = page;

        if (!Array.isArray(points)) {
            throw new Error(`Malformed points response for stream ${streamId}`);
        }

        allPoints.push(...points);
        if (cursor !== null) lastOccurredAt = cursor;

        // A short page means the window is drained. This trusts the array length rather than the
        // reported `count`, so the two disagreeing can't keep the loop spinning.
        if (points.length < PAGE_LIMIT) break;

        // Only a cursor that strictly advances narrows the window. Without this, an absent or
        // repeated cursor would re-send a byte-identical request forever.
        if (cursor === null || (pageStart !== undefined && cursor.getTime() <= pageStart.getTime())) break;

        // The next page would use `cursor` as an *exclusive* lower bound, so once it reaches
        // `windowEnd` the requested range contains no instants — which the API rejects.
        if (cursor.getTime() >= windowEnd.getTime()) break;

        pageStart = cursor;
    }

    // Points carry epoch-ms `t` from here on: mergePoints keys a Map<number>, so a Date
    // can't dedup by value downstream. In raw mode each point is its own bucket (count: 1,
    // avg=min=max=value). TODO: the API will eventually return this bucketed shape directly.
    const points: StreamPoint[] = allPoints.map((item) => ({
        t: new Date(item.occurredAt).getTime(),
        avg: item.convertedValue,
        min: item.convertedValue,
        max: item.convertedValue,
        count: 1,
    }));

    return { points, lastOccurredAt };
}
